import { Button, ButtonAnchor, Heading } from '@postscript/components';
import styled from 'styled-components';
import { useEffect, useRef } from 'react';
import { ACORN_ORIGIN } from 'utils/envVars';

const Page = styled.main`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: var(--spacing-4);
  padding: var(--spacing-6);
  text-align: center;
  background: var(--surface-bkg-color);
`;

interface Props {
  entryError?: string;
  isLogoutPending?: boolean;
  isLogoutInFlight?: boolean;
  onRetry?: () => void;
}

const LegacyHistoryExpired = ({
  onRetry,
  entryError,
  isLogoutPending,
  isLogoutInFlight,
}: Props): JSX.Element => {
  const page = useRef<HTMLElement>(null);
  useEffect(() => {
    page.current?.querySelector('h1')?.focus();
  }, []);
  return (
    <Page ref={page} aria-busy={isLogoutInFlight}>
      <Heading as="h1" size="large" tabIndex={-1}>
        {entryError
          ? 'Couldn’t open Postscript 1.0'
          : isLogoutInFlight
          ? 'Signing out of Postscript 1.0'
          : isLogoutPending
          ? 'Couldn’t end your Postscript 1.0 session'
          : onRetry
          ? 'Couldn’t restore your Postscript 1.0 session'
          : 'Your Postscript 1.0 session has ended'}
      </Heading>
      <p
        role={entryError ? 'alert' : 'status'}
        aria-live={entryError ? 'assertive' : 'polite'}
        aria-atomic="true"
      >
        {entryError ||
          (isLogoutInFlight
            ? 'Access is paused in this tab.'
            : isLogoutPending
            ? 'Access is paused in this tab. Try again to finish signing out.'
            : onRetry
            ? 'Check your connection and try again. Your session’s original expiration still applies.'
            : 'Return to Postscript 2.0 to open your read-only history again.')}
      </p>
      {onRetry && !isLogoutInFlight && (
        <Button onClick={onRetry}>Try again</Button>
      )}
      <ButtonAnchor role="link" href={ACORN_ORIGIN}>
        Return to Postscript 2.0
      </ButtonAnchor>
    </Page>
  );
};

export default LegacyHistoryExpired;
